import React, { useEffect, useRef, useState } from "react";

export default function Services() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const services = [
    {
      title: "Pengiriman Darat",
      subtitle: "Land Delivery",
      description:
        "Kami menyediakan layanan untuk pengiriman melalui jalur darat dengan waktu pengiriman standard dan expres yang dapat disesuaikan dengan kebutuhan.",
      icon: (
        <svg
          className="w-10 h-10 text-white"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
      color: "from-primary to-secondary",
    },
    {
      title: "Kiriman Kargo Besar",
      subtitle: "Large Cargo Delivery",
      description:
        "Tersedia layanan untuk mengirim barang berukuran besar dan berat yang membutuhkan penanganan khusus.",
      icon: (
        <svg
          className="w-10 h-10 text-white"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          />
        </svg>
      ),
      color: "from-secondary to-primary",
    },
    {
      title: "Pengiriman Rutin & Massal",
      subtitle: "Routine and Bulk Shipments",
      description:
        "Tersedia layanan untuk mengirimkan barang secara konsisten, rutin, berkala, terjadwal yang membutuhkan pengiriman barang dalam jumlah besar sekaligus.",
      icon: (
        <svg
          className="w-10 h-10 text-white"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      color: "from-primary to-secondary",
    },
  ];

  return (
    <section
      id="services"
      ref={sectionRef}
      className="py-32 bg-white relative overflow-hidden"
    >
      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="text-center mb-20">
          <h2
            className={`text-5xl lg:text-6xl font-bold text-gradient mb-6 transition-all duration-1000 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-20"
            }`}
          >
            Layanan Kami
          </h2>
          <p
            className={`text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed transition-all duration-1000 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-20"
            }`}
            style={{ transitionDelay: "0.1s" }}
          >
            PT Berkat Mandiri Expedisi memiliki berbagai macam layanan untuk
            memenuhi kebutuhan pengiriman Anda
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {services.map((service, index) => (
            <div
              key={index}
              className={`group bg-white rounded-3xl shadow-xl p-10 card-premium border border-gray-100 transition-all duration-1000 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-20"
              }`}
              style={{ transitionDelay: `${0.2 + index * 0.15}s` }}
            >
              <div
                className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-8 mx-auto glow-lg group-hover:scale-110 transition-transform duration-500`}
              >
                {service.icon}
              </div>
              <h3 className="text-2xl font-bold text-primary mb-2 text-center">
                {service.title}
              </h3>
              <p className="text-sm text-gray-500 mb-6 text-center italic font-light">
                {service.subtitle}
              </p>
              <p className="text-gray-700 text-center leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>

        <div
          className={`mt-20 relative rounded-3xl overflow-hidden shadow-2xl transition-all duration-1000 ${
            isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
          }`}
          style={{ transitionDelay: "0.6s" }}
        >
          <div className="gradient-mesh p-16 text-center relative overflow-hidden noise">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-1/4 w-64 h-64 bg-white rounded-full blur-3xl animate-float"></div>
              <div
                className="absolute bottom-0 right-1/4 w-80 h-80 bg-white rounded-full blur-3xl animate-float"
                style={{ animationDelay: "2s" }}
              ></div>
            </div>
            <div className="relative z-10">
              <h3 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                Cakupan Seluruh Pulau Jawa
              </h3>
              <p className="text-xl text-white/90 mb-8 font-light">
                Pengiriman Cepat, Aman, dan Terpercaya
              </p>
              <a
                href="#contact"
                className="inline-block px-10 py-5 bg-white text-primary rounded-full font-bold text-sm uppercase tracking-wider btn-premium shadow-2xl"
              >
                Konsultasi Gratis
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
